/**
 * UserSelectItemView - Displays a selectable user row with checkbox and avatar
 *
 * Renders a checkbox, initials avatar circle, user name, and job title.
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import { CheckIcon } from "lucide-react-native";
import { memo } from "react";

import { Box } from "@/components/atoms/Box";
import {
  Checkbox,
  CheckboxIcon,
  CheckboxIndicator,
} from "@/components/atoms/Checkbox";
import { Stack } from "@/components/atoms/Stack";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

/** Style for the avatar circle */
/**
 * Returns the foreground text class.
 * Theme-aware via the content-primary token (was an isDark white/zinc-900
 * ternary).
 * @param _isDark - Unused; retained for the call-site signature
 */
const getFgTextClass = (_isDark: boolean): string => "text-content-primary";

/**
 * Returns the muted text class.
 * Theme-aware via the content-muted token (was an isDark zinc-400/zinc-500
 * ternary).
 * @param _isDark - Unused; retained for the call-site signature
 */
const getMutedTextClass = (_isDark: boolean): string => "text-content-muted";

/**
 * Returns the border class.
 * Theme-aware via the outline-subtle token (was an isDark
 * zinc-700/zinc-200 ternary).
 * @param _isDark - Unused; retained for the call-site signature
 */
const getBorderClass = (_isDark: boolean): string => "border-outline-subtle";

/**
 * Returns the avatar background class.
 * Theme-aware via the surface-subtle token (was an isDark
 * zinc-700/zinc-200 ternary).
 * @param _isDark - Unused; retained for the call-site signature
 */
const getAvatarBgClass = (_isDark: boolean): string => "bg-surface-subtle";

/** Props for UserSelectItemView */
interface UserSelectItemViewProps {
  /** Display name for the user */
  readonly displayName: string;
  /** Initials for the avatar circle */
  readonly initials: string;
  /** Job title or role */
  readonly title: string;
  /** Whether this user is currently selected */
  readonly isSelected: boolean;
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Callback when the row is pressed */
  readonly onPress: () => void;
}

/**
 * Renders a single user row with checkbox, avatar, name, and title
 * @param root0 - Component props
 * @param root0.displayName - Display name for the user
 * @param root0.initials - Initials for the avatar circle
 * @param root0.title - Job title or role
 * @param root0.isSelected - Whether this user is currently selected
 * @param root0.isDark - Whether dark mode is active
 * @param root0.onPress - Callback when the row is pressed
 */
const UserSelectItemView = ({
  displayName,
  initials,
  title,
  isSelected,
  isDark,
  onPress,
}: UserSelectItemViewProps) => (
  <Pressable onPress={onPress} className="py-3">
    <Stack
      direction="horizontal"
      className={`items-center gap-3 border-b pb-3 ${getBorderClass(isDark)}`}
    >
      <Box className="pointer-events-none">
        <Checkbox value={displayName} isChecked={isSelected} size="md">
          <CheckboxIndicator>
            <CheckboxIcon as={CheckIcon} />
          </CheckboxIndicator>
        </Checkbox>
      </Box>

      {/* Avatar circle with initials */}
      <Box
        className={`size-10 items-center justify-center rounded-full ${getAvatarBgClass(isDark)}`}
      >
        <Text variant="body-strong" className={getMutedTextClass(isDark)}>
          {initials}
        </Text>
      </Box>

      {/* Name and title */}
      <Stack className="flex-1">
        <Text
          variant="body"
          className={`font-medium ${getFgTextClass(isDark)}`}
        >
          {displayName}
        </Text>
        {!!title && (
          <Text variant="caption" className={getMutedTextClass(isDark)}>
            {title}
          </Text>
        )}
      </Stack>
    </Stack>
  </Pressable>
);

UserSelectItemView.displayName = "UserSelectItemView";

export default memo(UserSelectItemView);
